'use client';

import React, { useEffect, useState } from 'react';
import { X, Mail, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { chatCreateConversation } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

/** Topic options surfaced in the contact-form dropdown. The wire value is
 *  stable; the label is what the user sees. When a trigger dispatches the
 *  `open-contact` custom event with `detail.topic`, the form preselects it. */
export type ContactTopic =
  | 'general'
  | 'upgrade'
  | 'cancel'
  | 'billing'
  | 'feature'
  | 'bug'
  | 'partnership'
  | 'other';

const TOPIC_OPTIONS: { value: ContactTopic; label: string }[] = [
  { value: 'general',     label: 'General question' },
  { value: 'upgrade',     label: 'Upgrade to a paid plan' },
  { value: 'cancel',      label: 'Cancel subscription' },
  { value: 'billing',     label: 'Billing question' },
  { value: 'feature',     label: 'Feature request' },
  { value: 'bug',         label: 'Bug report' },
  { value: 'partnership', label: 'Partnership / business' },
  { value: 'other',       label: 'Other' },
];

const TOPIC_LABELS: Record<ContactTopic, string> = Object.fromEntries(
  TOPIC_OPTIONS.map((t) => [t.value, t.label]),
) as Record<ContactTopic, string>;

/** Dispatch this anywhere to open the contact modal from a trigger.
 *  `topic` preselects the dropdown. */
export function openContactForm(topic: ContactTopic = 'general') {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('open-contact', { detail: { topic } }));
}

/** Mount once near the root (LayoutShell). Listens for the `open-contact`
 *  custom event and renders the modal when fired. Handles auth-aware
 *  prefill, validation, and submission to the internal messaging system
 *  (same channel the advertise page uses). */
export default function ContactFormModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState<ContactTopic>('general');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  // Refresh prefilled values whenever the modal opens or the signed-in user
  // changes. Logged-in users get these locked for consistency across
  // triggers; anonymous users can edit freely.
  useEffect(() => {
    if (user) {
      setName(user.displayName || user.username || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim());
      setEmail(user.email);
    }
  }, [user, open]);

  // Listen for the global open event.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      if (detail.topic && TOPIC_LABELS[detail.topic as ContactTopic]) {
        setTopic(detail.topic as ContactTopic);
      } else {
        setTopic('general');
      }
      setStatus('idle');
      setError('');
      setMessage('');
      setOpen(true);
    };
    window.addEventListener('open-contact', handler as EventListener);
    return () => window.removeEventListener('open-contact', handler as EventListener);
  }, []);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimName = name.trim();
    const trimEmail = email.trim();
    const trimMsg = message.trim();

    if (!trimName || !trimEmail) { setError('Name and email are required.'); return; }
    if (!trimEmail.includes('@')) { setError('Please enter a valid email.'); return; }
    if (!trimMsg) { setError('Please enter a message.'); return; }

    setStatus('sending');
    try {
      const topicLabel = TOPIC_LABELS[topic];
      const body = [
        `Topic: ${topicLabel}`,
        `Name: ${trimName}`,
        `Email: ${trimEmail}`,
        user ? `User ID: ${user.id}` : 'Anonymous visitor',
        '',
        'Message:',
        trimMsg,
      ].filter(Boolean).join('\n');

      await chatCreateConversation({
        message: body,
        guestName: user ? undefined : trimName,
        guestEmail: user ? undefined : trimEmail,
        subject: `[Contact · ${topicLabel}] ${trimName}`,
        deliveryMode: 'form',
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unable to send. Please try again.');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--card)] border border-[var(--border)] rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--border)]">
          <div className="w-8 h-8 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
            <Mail size={16} className="text-sky-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-[var(--foreground)]">Contact us</h2>
            <p className="text-xs text-[var(--foreground-muted)]">We typically respond within 1 business day.</p>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--foreground-muted)] hover:text-[var(--foreground)] hover:bg-[var(--background-tertiary)] transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {status === 'sent' ? (
          <div className="px-5 py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={22} className="text-emerald-400" />
            </div>
            <h3 className="text-base font-semibold text-[var(--foreground)] mb-1">Message sent</h3>
            <p className="text-sm text-[var(--foreground-secondary)] mb-5">Thanks — we&apos;ll get back to you at <strong>{email}</strong> soon.</p>
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Topic */}
            <div>
              <label className="block text-xs font-medium text-[var(--foreground-secondary)] mb-1.5">What can we help with?</label>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value as ContactTopic)}
                className="w-full bg-[var(--background)] border border-[var(--border)] focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none transition-colors"
              >
                {TOPIC_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-[var(--foreground-secondary)] mb-1.5">
                Name {user && <span className="text-[var(--foreground-muted)] font-normal">(from your account)</span>}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!!user}
                required
                maxLength={100}
                className={cn(
                  'w-full bg-[var(--background)] border border-[var(--border)] focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none transition-colors',
                  user && 'opacity-70 cursor-not-allowed',
                )}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-medium text-[var(--foreground-secondary)] mb-1.5">
                Email {user && <span className="text-[var(--foreground-muted)] font-normal">(from your account)</span>}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!user}
                required
                maxLength={200}
                className={cn(
                  'w-full bg-[var(--background)] border border-[var(--border)] focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] focus:outline-none transition-colors',
                  user && 'opacity-70 cursor-not-allowed',
                )}
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs font-medium text-[var(--foreground-secondary)] mb-1.5">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                maxLength={2000}
                placeholder="Tell us what you need…"
                className="w-full bg-[var(--background)] border border-[var(--border)] focus:border-sky-500 rounded-lg px-3 py-2.5 text-sm text-[var(--foreground)] placeholder-[var(--foreground-muted)] focus:outline-none transition-colors resize-none"
              />
              <p className="text-[10px] text-[var(--foreground-muted)] mt-1">{message.length}/2000</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 text-red-400 text-xs px-3 py-2 rounded-lg">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={status === 'sending'}
                className="flex items-center gap-2 px-4 py-2.5 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {status === 'sending' ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2.5 text-sm text-[var(--foreground-secondary)] hover:text-[var(--foreground)] transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
